import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Network } from "lucide-react";
import { toast } from "sonner";
import OrgChart from "@/components/OrgChart";

const STATUSES = ["Active","Draft","Archived"];

export default function OrganizationPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "administrator";
  const { data = [] } = useQuery({ queryKey:["organization"], queryFn: async () => (await apiClient.get("/organization")).data });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState("ALL");

  const options = useMemo(() => data.map(d => ({ id: d.id, label: `${d.position_name}${d.department?` · ${d.department}`:""}` })), [data]);
  const filtered = useMemo(() => data.filter(d => filterStatus === "ALL" || d.status === filterStatus).sort((a,b)=>(a.display_order||0)-(b.display_order||0)), [data, filterStatus]);

  const mSave = useMutation({
    mutationFn: async (b) => b.id ? (await apiClient.put(`/organization/${b.id}`, b)).data : (await apiClient.post("/organization", b)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["organization"]}); toast.success("Posisi disimpan"); setOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal"),
  });
  const mDel = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/organization/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["organization"]}); toast.success("Posisi dihapus"); },
  });

  const openNew = () => { setEditing({ position_name:"", department:"", parent_id: null, pic:"", description:"", display_order: (data.length+1)*10, status:"Active", effective_date: new Date().toISOString().slice(0,10), version: String(new Date().getFullYear()) }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="organization-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">Sistem</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Struktur Organisasi</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola hierarki posisi Flight Operation Group. Perubahan tersimpan sebagai versi baru.</p>
      </div>

      <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2" style={{fontFamily:"Outfit"}}><Network className="w-4 h-4 text-[#00A651]"/> Pratinjau Struktur</CardTitle></CardHeader>
        <CardContent><OrgChart/></CardContent>
      </Card>

      <Card className="border-slate-200"><CardContent className="p-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status"/></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Status</SelectItem>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isAdmin && <Button onClick={openNew} className="bg-[#00A651] hover:bg-[#008741]" data-testid="org-add-btn"><Plus className="w-4 h-4 mr-1"/> Tambah Posisi</Button>}
        </div>
        <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
          <Table className="fodm-table"><TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Order</TableHead><TableHead>Posisi</TableHead><TableHead>Departemen</TableHead><TableHead>Parent</TableHead><TableHead>PIC</TableHead><TableHead>Efektif</TableHead><TableHead>Versi</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead/>}
          </TableRow></TableHeader><TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={isAdmin?9:8} className="text-center py-8 italic text-slate-500">Belum ada posisi.</TableCell></TableRow>
            : filtered.map(o => {
              const parent = data.find(x => x.id === o.parent_id);
              return (
              <TableRow key={o.id}>
                <TableCell className="text-xs">{o.display_order}</TableCell>
                <TableCell className="font-semibold">{o.position_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{o.department}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{parent?.position_name || <span className="italic">— Top —</span>}</TableCell>
                <TableCell className="text-xs">{o.pic || "—"}</TableCell>
                <TableCell className="text-xs">{o.effective_date}</TableCell>
                <TableCell className="text-xs mono">{o.version}</TableCell>
                <TableCell><Badge className={o.status === "Active" ? "bg-emerald-100 text-emerald-700" : o.status === "Draft" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-700"}>{o.status}</Badge></TableCell>
                {isAdmin && <TableCell className="text-right"><div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" onClick={()=>{setEditing({...o}); setOpen(true);}}><Pencil className="w-4 h-4"/></Button>
                  <AlertDialog><AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600"><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                    <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus posisi?</AlertDialogTitle><AlertDialogDescription>Sebaiknya arsipkan alih-alih menghapus untuk menjaga riwayat.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(o.id)}>Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                  </AlertDialog>
                </div></TableCell>}
              </TableRow>
            );})}
          </TableBody></Table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah" : "Tambah"} Posisi</DialogTitle></DialogHeader>
          {editing && <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nama Posisi</Label><Input value={editing.position_name} onChange={(e)=>setEditing({...editing, position_name:e.target.value})}/></div>
            <div><Label>Departemen/Unit</Label><Input value={editing.department} onChange={(e)=>setEditing({...editing, department:e.target.value})}/></div>
            <div><Label>Parent</Label>
              <Select value={editing.parent_id || "NONE"} onValueChange={(v)=>setEditing({...editing, parent_id: v === "NONE" ? null : v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="NONE">— Top Level —</SelectItem>
                  {options.filter(o => o.id !== editing.id).map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>PIC / Pejabat (opsional)</Label><Input value={editing.pic || ""} onChange={(e)=>setEditing({...editing, pic:e.target.value})}/></div>
            <div><Label>Display Order</Label><Input type="number" value={editing.display_order} onChange={(e)=>setEditing({...editing, display_order:Number(e.target.value)})}/></div>
            <div><Label>Efektif</Label><Input type="date" value={editing.effective_date || ""} onChange={(e)=>setEditing({...editing, effective_date:e.target.value})}/></div>
            <div><Label>Versi (contoh: 2026)</Label><Input value={editing.version || ""} onChange={(e)=>setEditing({...editing, version:e.target.value})}/></div>
            <div><Label>Status</Label>
              <Select value={editing.status} onValueChange={(v)=>setEditing({...editing, status:v})}><SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="col-span-2"><Label>Deskripsi</Label><Input value={editing.description || ""} onChange={(e)=>setEditing({...editing, description:e.target.value})}/></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mSave.mutate(editing)}>Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
